/** Small browser helpers shared by UI components. */
import { effect, type ReadonlySignal } from '@preact/signals';
import type { Season } from '../season/season';

export function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * The device can buzz: the Vibration API on a touch screen. iPhones and iPads have no such
 * API, and computers expose it without anything to vibrate.
 */
export function canVibrate(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.vibrate === 'function' &&
    typeof matchMedia === 'function' &&
    matchMedia('(pointer: coarse)').matches
  );
}

/**
 * No pinch zoom in the app (what the viewport's user-scalable=no asks for, but iOS ignores):
 * a room zoomed by accident would carry the zoom into the race, where zoom gestures are
 * blocked, with half of the HUD off screen. Safari's own gesture events; in other browsers
 * touch-action does it (base.css).
 */
export function blockPinchZoom(doc: Pick<Document, 'addEventListener'>): void {
  doc.addEventListener('gesturestart', (event) => event.preventDefault(), { passive: false });
}

/**
 * Mirrors the holiday season the app shows on `data-season` of `root` (`<html>`; no
 * attribute out of season): the shell's holiday styles hang on it (ui/season/SeasonDecor.css).
 * Returns the disposer.
 */
export function syncSeasonAttribute(root: HTMLElement, season: ReadonlySignal<Season>): () => void {
  return effect(() => {
    const value = season.value;
    if (value === 'none') delete root.dataset.season;
    else root.dataset.season = value;
  });
}

/** Copies text to the clipboard; resolves false when the browser refuses. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
